import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { coursesAPI, signedIn } from "../../services/portalApi";
import Footer from "../home/footer";
import Picture from "../utils/Picture";
import "../booking/booking.css";
import "./courses.css";

const KIND = { video: "Video", text: "Reading", task: "Practice", quiz: "Reflection", download: "Download" };
const mins = (m) => (m ? (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60 ? `${m % 60} min` : ""}` : `${m} min`) : "");

/** YouTube / Vimeo links as an embeddable address; anything else plays as a file. */
const embed = (url) => {
  const u = String(url || "");
  const yt = u.match(/(?:youtu\.be\/|[?&]v=|youtube\.com\/(?:embed|shorts)\/)([\w-]{11})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

/** Progress as a thin bar plus words; the words carry the meaning. */
export const Progress = ({ p }) => (
  <div className="crs-progress">
    <span className="crs-bar" aria-hidden="true"><span style={{ width: `${p.total ? (100 * p.done) / p.total : 0}%` }} /></span>
    <span className="crs-progress-text">{p.complete ? "Completed" : `${p.done} of ${p.total} done`}</span>
  </div>
);

/**
 * One course, read in the guest's account: chapters with their lessons,
 * each opening in place, and "Mark as complete" at the end of each.
 * The content is written by the team in the shared course library.
 */
export default function CoursePage() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!signedIn()) { setError({ status: 401 }); return; }
    coursesAPI.get(id)
      .then((c) => {
        setCourse(c);
        // Open the first lesson not yet done
        const next = c.chapters.flatMap((ch) => ch.lessons).find((l) => !l.done);
        setOpen(next ? next.id : null);
      })
      .catch((err) => setError({ status: err.status, message: err.message }));
  }, [id]);

  const toggle = async (lesson) => {
    setSaving(lesson.id);
    try {
      const { progress } = await coursesAPI.done(course.id, lesson.id, !lesson.done);
      const lessons = course.chapters.flatMap((ch) => ch.lessons);
      setCourse((c) => ({
        ...c, progress,
        chapters: c.chapters.map((ch) => ({ ...ch, lessons: ch.lessons.map((l) => (l.id === lesson.id ? { ...l, done: !lesson.done } : l)) })),
      }));
      // Finishing a lesson moves on to the next one
      if (!lesson.done) {
        const i = lessons.findIndex((l) => l.id === lesson.id);
        const next = lessons.slice(i + 1).find((l) => !l.done);
        setOpen(next ? next.id : null);
      }
    } catch (err) {
      setError({ status: err.status, message: err.message, inline: true });
    } finally { setSaving(null); }
  };

  if (error && !error.inline) {
    return (
      <>
        <div className="mk-page grain-bg-light">
          <div className="mk-wrap">
            <p className="mk-eyebrow">Your account</p>
            <h1 className="mk-title">{error.status === 401 ? "Please sign in" : error.status === 403 ? "Not open yet" : "We couldn't open this course"}</h1>
            <p className="mk-muted">{error.status === 401 ? "Sign in to your account to see your courses." : error.message}</p>
            <div className="mk-actions"><Link className="mk-btn mk-btn--primary" to="/account">Back to your account</Link></div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="mk-page grain-bg-light">
        <div className="mk-wrap crs-wrap">
          <Link className="crs-back" to="/account">← Your account</Link>
          {!course ? <p className="mk-muted" style={{ minHeight: "60vh" }}>Loading…</p> : (
            <>
              <header className="crs-head">
                {course.thumbnail && (
                  <div className="crs-hero">
                    <Picture src={course.thumbnail} alt="" priority width={1200} height={500} sizes="(min-width: 900px) 860px, 100vw" />
                  </div>
                )}
                <p className="mk-eyebrow">Preparation</p>
                <h1 className="crs-title hFont">{course.title}</h1>
                {course.description && <p className="crs-desc">{course.description}</p>}
                <Progress p={course.progress} />
              </header>

              {error?.inline && <p className="mk-error" role="alert">{error.message}</p>}

              {course.chapters.map((ch, ci) => (
                <section key={ch.id} className="crs-chapter" aria-labelledby={`ch-${ch.id}`}>
                  <h2 id={`ch-${ch.id}`} className="crs-chapter-title">
                    {course.chapters.length > 1 && <span className="crs-chapter-num">{ci + 1}</span>}
                    {ch.title}
                  </h2>
                  {ch.description && <p className="crs-chapter-desc">{ch.description}</p>}
                  <ol className="crs-lessons">
                    {ch.lessons.map((l) => (
                      <Lesson key={l.id} l={l} open={open === l.id} onOpen={() => setOpen(open === l.id ? null : l.id)}
                        onToggle={() => toggle(l)} saving={saving === l.id} />
                    ))}
                  </ol>
                </section>
              ))}

              {course.progress.complete && (
                <div className="crs-finished" role="status">
                  <p className="mb-0"><b>You've finished this course.</b> Everything in it stays here to come back to.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

function Lesson({ l, open, onOpen, onToggle, saving }) {
  const ref = useRef(null);
  useEffect(() => { if (open && ref.current) ref.current.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [open]);
  return (
    <li className={`crs-lesson ${l.done ? "is-done" : ""} ${open ? "is-open" : ""}`} ref={ref}>
      <button type="button" className="crs-lesson-line" aria-expanded={open} onClick={onOpen}>
        <span className="crs-tick" aria-hidden="true">{l.done ? "✓" : ""}</span>
        <span className="crs-lesson-name">{l.title}</span>
        <span className="crs-lesson-meta">
          {KIND[l.type] || "Lesson"}{l.duration_min ? ` · ${mins(l.duration_min)}` : ""}{!l.is_required ? " · optional" : ""}
          <span className="visually-hidden">{l.done ? ", done" : ""}</span>
        </span>
      </button>
      {open && (
        <div className="crs-lesson-body">
          <LessonContent l={l} />
          <div className="mk-actions crs-lesson-actions">
            <button type="button" className={`mk-btn ${l.done ? "mk-btn--ghost" : "mk-btn--primary"}`} disabled={saving} onClick={onToggle} aria-pressed={l.done}>
              {saving ? "Saving…" : l.done ? "Mark as not done" : "Mark as complete"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function LessonContent({ l }) {
  const text = (t) => String(t || "").split("\n").map((line, i) => (line.trim() ? <p key={i}>{line}</p> : null));
  if (l.type === "video") {
    const src = embed(l.video_url);
    return (
      <>
        {src
          ? <div className="crs-video"><iframe src={src} title={l.title} allow="encrypted-media; picture-in-picture; fullscreen" allowFullScreen loading="lazy" /></div>
          : l.video_url ? <video className="crs-video-file" controls preload="metadata" src={l.video_url} /> : <p className="mk-muted">The video for this lesson is coming soon.</p>}
        {l.content && <div className="crs-text">{text(l.content)}</div>}
      </>
    );
  }
  if (l.type === "download") {
    return (
      <div className="crs-text">
        {text(l.content)}
        {l.video_url && <a className="mk-btn mk-btn--ghost" href={l.video_url} target="_blank" rel="noreferrer">Download</a>}
      </div>
    );
  }
  if (l.type === "quiz") return <Reflection content={l.content} />;
  return l.content ? <div className="crs-text">{text(l.content)}</div> : <p className="mk-muted">This lesson is coming soon.</p>;
}

/** Quiz lessons, kept gentle: choose, then see the suggested answer. Nothing is graded or stored. */
function Reflection({ content }) {
  let qs = [];
  try { qs = JSON.parse(content || "[]"); } catch (_) { qs = []; }
  const [answers, setAnswers] = useState({});
  const [shown, setShown] = useState(false);
  if (!qs.length) return <p className="mk-muted">This lesson is coming soon.</p>;
  return (
    <div className="crs-quiz">
      {qs.map((q, qi) => (
        <fieldset key={q.id || qi} className="crs-q">
          <legend>{q.question}</legend>
          {q.type === "multiple" ? (q.options || []).map((o) => (
            <label key={o.id} className={`crs-opt ${shown && q.correctId === o.id ? "is-right" : ""}`}>
              <input type="radio" name={`q-${q.id || qi}`} checked={answers[q.id] === o.id} onChange={() => setAnswers((a) => ({ ...a, [q.id]: o.id }))} />
              {o.text}
            </label>
          )) : (
            <textarea rows={3} className="crs-answer" aria-label="Your answer" value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
          )}
        </fieldset>
      ))}
      {qs.some((q) => q.type === "multiple") && !shown && <button type="button" className="mk-btn mk-btn--ghost" onClick={() => setShown(true)}>Show suggested answers</button>}
    </div>
  );
}
